'use client';

import { CaretDownIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
  /**
   * Day-cell size, e.g. `'2rem'` — every other metric in the grid derives from
   * it. Applied as an inline custom property ON PURPOSE: the default below is
   * responsive (`md:`), and a `md:` rule beats an unprefixed `[--cell-size:…]`
   * utility from a caller, so `className="[--cell-size:2rem]"` silently had no
   * effect at desktop. An inline style outranks both, at every breakpoint.
   */
  cellSize?: string;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'flat',
  cellSize,
  style,
  formatters,
  components,
  ...props
}: CalendarProps): React.ReactNode {
  const defaults = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      /* `--cell-size` is the single knob every metric below derives from —
         nav buttons, caption height, day cells and week-number gutter all
         read it, so a caller resizes the whole grid with one variable
         (`className="[--cell-size:2rem]"`) instead of overriding six
         `classNames` keys by hand. Matches upstream shadcn's calendar, which
         moved to this model precisely because per-key overrides drifted out
         of proportion with each other. */
      /* Default matches what the day cells actually measured before
         `--cell-size` existed (the icon button's 44px mobile / 40px desktop),
         so every existing caller renders identically; callers that want a
         denser grid now set one variable instead of six classNames. */
      /* `bg-transparent`, not `bg-background`. Every caller renders this inside
         a popover or dialog, whose surface is `bg-popover` — a different
         colour — so painting `bg-background` here drew a visible darker
         rectangle INSIDE the panel: a box within a box, measured at
         rgb(16,16,18) on a rgb(24,24,27) popover. The container owns the
         surface; the grid just sits on it. */
      className={cn(
        'group/calendar bg-transparent p-3 [--cell-size:2.75rem] md:[--cell-size:2.5rem]',
        className,
      )}
      style={cellSize ? ({ ...style, '--cell-size': cellSize } as React.CSSProperties) : style}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaults.months),
        month: cn('flex w-full flex-col gap-4', defaults.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaults.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaults.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaults.button_next,
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaults.month_caption,
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaults.dropdowns,
        ),
        dropdown_root: cn(
          'relative rounded-md border border-input shadow-xs',
          defaults.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 bg-popover opacity-0', defaults.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label'
            ? 'text-sm'
            : 'flex h-(--cell-size) items-center gap-1 rounded-md pr-1 pl-2 text-sm',
          defaults.caption_label,
        ),
        // react-day-picker v9 renamed this key `table` → `month_grid`. It was
        // never renamed here, so `w-full` was silently dropped on the floor and
        // the grid sized to its intrinsic content instead of its container —
        // which is why every caller that wanted a full-width calendar had to
        // re-force `root: 'w-full'` by hand and still got a grid narrower than
        // the controls beneath it.
        month_grid: cn('w-full border-collapse', defaults.month_grid),
        weekdays: cn('flex', defaults.weekdays),
        weekday: cn(
          // `bg-transparent` is load-bearing: these render as `<th>`, and the
          // app's global table styling fills a header cell (measured
          // rgba(39,39,42,.3)), which put a grey chip behind every one of
          // S M T W T F S. Weekday labels are a legend, not controls.
          'flex-1 bg-transparent rounded-md text-[0.8rem] font-normal text-muted-foreground select-none',
          defaults.weekday,
        ),
        // `bg-transparent`: weeks render as `<tr>`, and the app's global table
        // styling zebra-stripes alternating rows (measured rgba(39,39,42,.1)),
        // which banded the month grid like a data table.
        week: cn('mt-2 flex w-full bg-transparent', defaults.week),
        week_number_header: cn('w-(--cell-size) select-none', defaults.week_number_header),
        week_number: cn('text-[0.8rem] text-muted-foreground select-none', defaults.week_number),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none',
          defaults.day,
        ),
        range_start: cn('rounded-l-md bg-accent', defaults.range_start),
        range_middle: cn('rounded-none', defaults.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaults.range_end),
        today: cn('rounded-md bg-accent text-accent-foreground', defaults.today),
        outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaults.outside),
        disabled: cn('text-muted-foreground opacity-50', defaults.disabled),
        hidden: cn('invisible', defaults.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className: chevClassName, orientation, ...chevProps }) => {
          if (orientation === 'left') {
            return <CaretLeftIcon className={cn('size-4', chevClassName)} {...chevProps} />;
          }
          if (orientation === 'right') {
            return <CaretRightIcon className={cn('size-4', chevClassName)} {...chevProps} />;
          }
          return <CaretDownIcon className={cn('size-4', chevClassName)} {...chevProps} />;
        },
        DayButton: ({ className: dayClassName, day, modifiers, children, ...dayProps }) => {
          const ref = React.useRef<HTMLButtonElement>(null);
          React.useEffect(() => {
            if (modifiers.focused) ref.current?.focus();
          }, [modifiers.focused]);

          return (
            <Button
              ref={ref}
              variant="flat"
              size="icon"
              data-day={day.date.toLocaleDateString()}
              data-selected-single={
                modifiers.selected &&
                !modifiers.range_start &&
                !modifiers.range_end &&
                !modifiers.range_middle
              }
              className={cn(
                // `md:size-auto md:w-full` is load-bearing: Button's `icon`
                // size ships `md:h-10 md:w-10`, and a `md:` rule outranks the
                // unprefixed `size-auto w-full` here, so at desktop every day
                // cell silently pinned itself to 40px and ignored both its
                // container's width and `--cell-size`. That is why a
                // full-width calendar overflowed any container narrower than
                // 7 × 40px instead of shrinking to fit.
                'relative flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 rounded-md leading-none font-normal md:size-auto md:w-full',
                'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground',
                'data-[range-start=true]:rounded-l-md data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground',
                'data-[range-end=true]:rounded-r-md data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground',
                'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground',
                dayClassName,
              )}
              {...dayProps}
            >
              {children}
              {/* Opt-in event indicator: callers pass modifiers={{ marked: […] }}
                  to dot dates that already have something (e.g. scheduled posts).
                  bg-current inherits the day's text colour so it stays visible on
                  both selected (primary) and normal days. */}
              {modifiers.marked ? (
                <span
                  className="absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-current opacity-60"
                  aria-hidden
                />
              ) : null}
            </Button>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
